/**
 * PD-17377 — each storefront's own account messages. There are three, and
 * each has an email and a text that carry the same kind of link:
 *  - welcome: the link to set a password;
 *  - password reset: the link to choose a new password;
 *  - sign-in: a six-digit code and a link that signs the person straight in.
 *
 * Every storefront (the brand of a portal site) owns a copy of all six,
 * generated with today's built-in wording when the brand is created, and
 * edited on that brand's page. There is deliberately no tenant-wide template:
 * a tenant with two storefronts has two independent sets. CRM staff, and
 * customers of a company without a storefront, keep Workd's built-in wording,
 * which is not editable.
 *
 * Texts go out only while phone sign-in is on for that kind of user (the login
 * server decides; see apps/login platform-notification.handler).
 *
 * This file holds what the server that sends, the server that saves and the
 * browser preview must agree on, so the three can never disagree: the kinds
 * of message, their merge variables, the generated wording, and for texts the
 * renderer, the save rules and the segment count. It has no dependencies, so
 * crm-api, login and the browser all import it as is.
 */

/** The three storefront account messages; each has an email and a text. */
export type StorefrontMessageKind = 'welcome' | 'password_reset' | 'sign_in';

export const STOREFRONT_MESSAGE_KINDS: ReadonlyArray<StorefrontMessageKind> = [
	'welcome',
	'password_reset',
	'sign_in',
];

/** A storefront email, named for its message. */
export type StorefrontEmailMessageKind = StorefrontMessageKind;

export const STOREFRONT_EMAIL_MESSAGE_KINDS: ReadonlyArray<StorefrontEmailMessageKind> =
	STOREFRONT_MESSAGE_KINDS;

/**
 * A storefront text, named for its message. PD-17379 adds a text of its own
 * with no email: the sign-in code text the Android app is sent, short enough
 * for the app to fill the code in.
 */
export type StorefrontTextMessageKind = StorefrontMessageKind | 'sign_in_android';

export const STOREFRONT_TEXT_MESSAGE_KINDS: ReadonlyArray<StorefrontTextMessageKind> =
	[...STOREFRONT_MESSAGE_KINDS, 'sign_in_android'];

/** A merge variable a storefront message can use, as `{{name}}`. */
export interface StorefrontMessageVariableModel {
	name: string;
	label: string;
	description: string;
	/** What previews show in its place. */
	sampleValue: string;
}

/** The variables every storefront text can use. */
const COMMON_TEXT_VARIABLES: ReadonlyArray<StorefrontMessageVariableModel> = [
	{
		name: 'brandName',
		label: 'Brand name',
		description: "The storefront's brand name, as customers see it.",
		sampleValue: 'Your Brand',
	},
	{
		name: 'firstName',
		label: 'First name',
		description:
			"The customer's first name. Blank when the account has none, so write the text to read well without it.",
		sampleValue: 'Jordan',
	},
	{
		name: 'lastName',
		label: 'Last name',
		description: "The customer's last name. Blank when the account has none.",
		sampleValue: 'Lee',
	},
	{
		name: 'supportPhone',
		label: 'Support phone',
		description: "The brand's customer support phone number, when it has one.",
		sampleValue: '(555) 010-0100',
	},
	{
		name: 'supportEmail',
		label: 'Support email',
		description: "The brand's customer support email address, when it has one.",
		sampleValue: 'support@example.com',
	},
];

/**
 * A shortened storefront link, the shape a text really carries
 * (`https://<storefront>/s/<8 characters>`), so the preview's segment count is
 * close to what is sent. The storefront's own address can make it a little
 * longer or shorter.
 */
const SAMPLE_SHORT_LINK = 'https://shop.example.com/s/Ab3dE9xQ';

/** The sample code previews show. */
export const STOREFRONT_SAMPLE_CODE = '482913';

/** The storefront host previews name in a sign-in text's autofill line. */
export const STOREFRONT_SAMPLE_OTP_HOST = 'shop.example.com';

const SET_PASSWORD_LINK_VARIABLE: StorefrontMessageVariableModel = {
	name: 'setPasswordLink',
	label: 'Set-password link',
	description:
		'The link to the page where the customer sets their password. It works once and expires in 24 hours.',
	sampleValue: SAMPLE_SHORT_LINK,
};

const RESET_LINK_VARIABLE: StorefrontMessageVariableModel = {
	name: 'resetLink',
	label: 'Reset link',
	description:
		'The link to the page where the customer chooses a new password. It works once and expires in 24 hours.',
	sampleValue: SAMPLE_SHORT_LINK,
};

const SIGN_IN_LINK_VARIABLE: StorefrontMessageVariableModel = {
	name: 'signInLink',
	label: 'Sign-in link',
	description:
		'The link that signs the customer straight in on the device where they open it. It works once and expires in 15 minutes, like the code.',
	sampleValue: SAMPLE_SHORT_LINK,
};

const CODE_VARIABLE: StorefrontMessageVariableModel = {
	name: 'code',
	label: 'Code',
	description:
		'The six-digit sign-in code. It works once and expires in 15 minutes.',
	sampleValue: STOREFRONT_SAMPLE_CODE,
};

/** One storefront text: what it is for, its variables and its generated wording. */
export interface StorefrontTextMessageDefinitionModel {
	kind: StorefrontTextMessageKind;
	label: string;
	/** When the text is sent, in the words the Brand page shows. */
	description: string;
	variables: ReadonlyArray<StorefrontMessageVariableModel>;
	/**
	 * Variables the text must keep. Our registered business-texting (A2P)
	 * campaign describes these texts as carrying the link or code, so a text
	 * without them would no longer match what the carriers approved.
	 */
	requiredVariables: ReadonlyArray<string>;
	/** The variable that holds the message's link; null for a text without one. */
	linkVariable: string | null;
	/**
	 * Today's built-in wording (apps/crm-api authorization-sms-messages.ts),
	 * expressed with variables. Rendering it gives exactly the built-in text
	 * (for the sign-in text, before the autofill line the sender adds); a test
	 * in crm-api holds the two together.
	 */
	defaultBody: string;
}

export const STOREFRONT_TEXT_MESSAGE_DEFINITIONS: ReadonlyArray<StorefrontTextMessageDefinitionModel> =
	[
		{
			kind: 'welcome',
			label: 'Welcome text',
			description:
				'Sent to a new customer added with a mobile number. The link opens the page where they set their password, through the app page when the storefront has an app.',
			variables: [...COMMON_TEXT_VARIABLES, SET_PASSWORD_LINK_VARIABLE],
			requiredVariables: ['setPasswordLink'],
			linkVariable: 'setPasswordLink',
			defaultBody:
				'Welcome to {{brandName}}! Your account is ready.\n\nTap to set your password: {{setPasswordLink}}\n\nThis link works once and expires in 24 hours. If you did not expect this, ignore this text.',
		},
		{
			kind: 'password_reset',
			label: 'Password reset text',
			description:
				'Sent when a customer asks to reset their password by text. The link opens the page where they choose a new one.',
			variables: [...COMMON_TEXT_VARIABLES, RESET_LINK_VARIABLE],
			requiredVariables: ['resetLink'],
			linkVariable: 'resetLink',
			defaultBody:
				'Reset your {{brandName}} password: {{resetLink}}\n\nThis link works once and expires in 24 hours. If you did not ask for this, ignore this text.',
		},
		{
			kind: 'sign_in',
			label: 'Sign-in code text',
			description:
				'Sent when a customer asks for a sign-in code by text. They type the code, or tap the link on the phone where it arrives. The line phones use to fill in the code is added at the end for you.',
			variables: [
				...COMMON_TEXT_VARIABLES,
				CODE_VARIABLE,
				SIGN_IN_LINK_VARIABLE,
			],
			requiredVariables: ['code', 'signInLink'],
			linkVariable: 'signInLink',
			defaultBody:
				'Your {{brandName}} sign-in code is {{code}}.\n\nOn this phone? Tap to sign in: {{signInLink}}\n\nIt expires in 15 minutes. If you did not try to sign in, ignore this text.',
		},
		{
			// PD-17379 — Android hands the app only a text of at most 140 bytes,
			// so this one has no link (the app cannot open it anyway) and a short
			// default. The brand can turn it off, and the app is then sent the
			// sign-in code text above.
			kind: 'sign_in_android',
			label: 'Android app sign-in code text',
			description:
				'Sent instead of the sign-in code text when the customer asks for the code in the storefront\'s Android app. Android fills the code in by itself only when the whole text is short, so this one has no link. The line phones use to fill in the code is added at the end for you.',
			variables: [...COMMON_TEXT_VARIABLES, CODE_VARIABLE],
			requiredVariables: ['code'],
			linkVariable: null,
			defaultBody: 'Your {{brandName}} sign-in code is {{code}}. It expires in 15 minutes.',
		},
	];

/** The definition of one storefront text. */
export function storefrontTextMessageDefinition(
	kind: StorefrontTextMessageKind,
): StorefrontTextMessageDefinitionModel {
	const found = STOREFRONT_TEXT_MESSAGE_DEFINITIONS.find(
		(definition) => definition.kind === kind,
	);
	if (found == null) {
		// The kinds and the definitions are one list; this is unreachable
		// unless someone adds a kind without its definition.
		throw new Error(`No storefront text definition for ${kind}`);
	}
	return found;
}

export function isStorefrontMessageKind(
	value: unknown,
): value is StorefrontMessageKind {
	return (
		typeof value === 'string' &&
		(STOREFRONT_MESSAGE_KINDS as ReadonlyArray<string>).includes(value)
	);
}

export function isStorefrontTextMessageKind(
	value: unknown,
): value is StorefrontTextMessageKind {
	return (
		typeof value === 'string' &&
		(STOREFRONT_TEXT_MESSAGE_KINDS as ReadonlyArray<string>).includes(value)
	);
}

/** The texts that carry a sign-in code, and so end with the autofill lines. */
export function isStorefrontSignInText(kind: StorefrontTextMessageKind): boolean {
	return kind === 'sign_in' || kind === 'sign_in_android';
}

export function isStorefrontEmailMessageKind(
	value: unknown,
): value is StorefrontEmailMessageKind {
	return isStorefrontMessageKind(value);
}

/**
 * The line iOS and Android read to offer a texted code for autofill on a
 * site: `@host #code`, after a blank line. It has to be the LAST line of the
 * text, which is why the sender adds it after rendering rather than leaving it
 * in an editable template. Nothing is added without a host.
 */
export function appendSmsOtpLine(
	text: string,
	code: string,
	otpHost: string | null | undefined,
): string {
	return appendSmsAutofillLines(text, code, otpHost);
}

/**
 * PD-17379 — the lines a code text ends with so phones can fill the code in:
 * the Android storefront app's 11-character hash (Google's SMS Retriever only
 * hands the app a text that carries it), then the `@host #code` line, which
 * iOS and Chrome's one-tap prompt need as the LAST line. The two sit together
 * after a blank line. Without a hash this is exactly `appendSmsOtpLine`.
 */
export function appendSmsAutofillLines(
	text: string,
	code: string,
	otpHost: string | null | undefined,
	androidAppHash?: string | null,
): string {
	const lines: Array<string> = [];
	if (androidAppHash != null && androidAppHash !== '') {
		lines.push(androidAppHash);
	}
	if (otpHost != null && otpHost !== '') {
		lines.push(`@${otpHost} #${code}`);
	}
	return lines.length === 0 ? text : `${text}\n\n${lines.join('\n')}`;
}

// --- Plain-text rendering (texts and email subject lines) ---

/**
 * A merge tag in a plain-text message: `{{name}}`, spaces allowed inside the
 * braces. Only plain names are supported. Sections, partials and
 * triple braces (`{{#x}}`, `{{>x}}`, `{{{x}}}`) are refused on save, because a
 * text has no HTML to escape and a hidden section could drop the link the
 * carriers require.
 */
const TAG_PATTERN = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;

/** Any `{{...}}` at all, for finding tags the pattern above does not accept. */
const ANY_TAG_PATTERN = /\{\{([^{}]*)\}\}/g;

export type StorefrontMessageValuesModel = Readonly<
	Record<string, string | null | undefined>
>;

/**
 * Fill a plain-text message's `{{variables}}`. Values go in exactly as given:
 * a text is not HTML, so `&` stays `&`, and a value that itself contains
 * `{{...}}` is not filled again. A variable with no value, or one this
 * message does not know, becomes empty.
 */
export function renderStorefrontPlainText(
	body: string,
	values: StorefrontMessageValuesModel,
): string {
	return body.replace(TAG_PATTERN, (_match, name: string) => {
		// Own properties only, so `{{constructor}}` can never read a prototype.
		if (!Object.prototype.hasOwnProperty.call(values, name)) {
			return '';
		}
		return values[name] ?? '';
	});
}

/** The variable names a plain-text message uses, in order, without repeats. */
export function storefrontPlainTextVariables(body: string): Array<string> {
	const names: Array<string> = [];
	for (const match of body.matchAll(TAG_PATTERN)) {
		const name = match[1];
		if (name != null && !names.includes(name)) {
			names.push(name);
		}
	}
	return names;
}

/**
 * The longest text template accepted. Twilio refuses a message over 1,600
 * characters; the cap leaves room for the brand name, the customer's name and
 * the link that fill the variables, and keeps a text to a handful of
 * segments.
 */
export const STOREFRONT_TEXT_MAX_LENGTH = 1000;

export type StorefrontTextProblemCode =
	| 'empty'
	| 'too_long'
	| 'missing_variable'
	| 'missing_brand_name'
	| 'unknown_variable'
	| 'unsupported_tag';

export interface StorefrontTextProblemModel {
	code: StorefrontTextProblemCode;
	/** Plain-language reason, shown on the Brand page and returned by the API. */
	message: string;
	/** The variable or tag the problem is about, when there is one. */
	detail?: string;
}

/**
 * Why a storefront text cannot be saved, or an empty list when it can.
 *
 * The text must keep its link or code variable and the brand name (the
 * variable, or the brand's name written out): our registered business-texting
 * campaign tells the carriers every account text names the business and
 * carries the link or code, and texts that stop doing so can be blocked. The
 * server refuses a save that fails these rules, and the browser shows the same
 * reasons before the save is attempted.
 */
export function validateStorefrontTextTemplate(
	kind: StorefrontTextMessageKind,
	body: string,
	brandName: string | null | undefined,
): Array<StorefrontTextProblemModel> {
	const definition = storefrontTextMessageDefinition(kind);
	const problems: Array<StorefrontTextProblemModel> = [];
	// The reasons name the text being edited ("Every welcome text must…").
	const messageName = definition.label.toLowerCase();

	if (body.trim() === '') {
		return [{ code: 'empty', message: 'Write the text before saving it.' }];
	}

	if (body.length > STOREFRONT_TEXT_MAX_LENGTH) {
		problems.push({
			code: 'too_long',
			message: `Keep the text to ${STOREFRONT_TEXT_MAX_LENGTH} characters or fewer.`,
		});
	}

	if (/\{\{\{|\}\}\}/.test(body)) {
		problems.push({
			code: 'unsupported_tag',
			message:
				'Use variables with two braces, like {{brandName}}. Three braces are not supported in texts.',
			detail: '{{{',
		});
	}

	const allowed = definition.variables.map((variable) => variable.name);
	for (const match of body.matchAll(ANY_TAG_PATTERN)) {
		const inner = match[1] ?? '';
		const name = inner.trim();
		if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
			problems.push({
				code: 'unsupported_tag',
				message: `${match[0]} is not a variable this text can use. Insert variables from the list.`,
				detail: match[0],
			});
			continue;
		}
		if (!allowed.includes(name)) {
			problems.push({
				code: 'unknown_variable',
				message: `{{${name}}} is not a variable this text can use.`,
				detail: name,
			});
		}
	}

	// A `{{` or `}}` left over once every complete tag is removed is a tag
	// that was never closed (or opened), which would otherwise go out as is.
	const leftover = body.replace(ANY_TAG_PATTERN, '');
	if (/\{\{|\}\}/.test(leftover)) {
		problems.push({
			code: 'unsupported_tag',
			message:
				'A variable is missing a brace. Each one starts with {{ and ends with }}.',
		});
	}

	const used = storefrontPlainTextVariables(body);
	for (const required of definition.requiredVariables) {
		if (!used.includes(required)) {
			problems.push({
				code: 'missing_variable',
				message: `Keep {{${required}}} in the text. Every ${messageName} must carry its ${required === 'code' ? 'code' : 'link'}.`,
				detail: required,
			});
		}
	}

	const name = brandName?.trim() ?? '';
	const namesBrand =
		used.includes('brandName') ||
		(name !== '' && body.toLowerCase().includes(name.toLowerCase()));
	if (!namesBrand) {
		problems.push({
			code: 'missing_brand_name',
			message: `Keep the brand name in the text, as {{brandName}} or written out. Every ${messageName} must name the business it comes from.`,
		});
	}

	return problems;
}

// --- SMS segments ---

/**
 * The GSM 03.38 default alphabet, the characters a text can carry at 7 bits
 * each. A text with any other character (an emoji, a curly quote, most
 * accented capitals) is sent as UCS-2 at 16 bits per character instead, which
 * is why one character can more than double a text's cost.
 */
const GSM_BASIC =
	'@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

/** GSM extension characters, each sent as an escape plus the character (2 septets). */
const GSM_EXTENSION = '^{}\\[~]|€\f';

export type SmsEncodingModel = 'GSM-7' | 'UCS-2';

export interface SmsSegmentCountModel {
	/** Characters as the person reading the text counts them. */
	characters: number;
	encoding: SmsEncodingModel;
	/** Message parts the carrier bills; 0 for an empty text. */
	segments: number;
	/** How many units each part holds at this length (160/153 or 70/67). */
	perSegment: number;
	/**
	 * Units used: septets for GSM-7 (an extension character counts twice),
	 * UTF-16 code units for UCS-2.
	 */
	units: number;
}

/**
 * How many SMS segments a text is sent as. A single GSM-7 text holds 160
 * septets; a longer one is split into parts of 153, since each part carries a
 * header that joins them back together. UCS-2 holds 70, or 67 per part.
 */
export function countSmsSegments(text: string): SmsSegmentCountModel {
	const characters = Array.from(text).length;
	let septets = 0;
	let isGsm = true;

	for (const character of text) {
		if (GSM_BASIC.includes(character)) {
			septets += 1;
		} else if (GSM_EXTENSION.includes(character)) {
			septets += 2;
		} else {
			isGsm = false;
			break;
		}
	}

	if (isGsm) {
		const perSegment = septets <= 160 ? 160 : 153;
		return {
			characters,
			encoding: 'GSM-7',
			segments: septets === 0 ? 0 : Math.ceil(septets / perSegment),
			perSegment,
			units: septets,
		};
	}

	// UCS-2 counts UTF-16 code units: an emoji outside the basic plane takes two.
	const units = text.length;
	const perSegment = units <= 70 ? 70 : 67;
	return {
		characters,
		encoding: 'UCS-2',
		segments: units === 0 ? 0 : Math.ceil(units / perSegment),
		perSegment,
		units,
	};
}

/** The 8-character code of the sample short link. */
const SAMPLE_SHORT_LINK_CODE = 'Ab3dE9xQ';

/**
 * The sample values a text preview fills its variables with. With the
 * storefront's own host (PD-17379), the sample link is shortened onto it the
 * way a real one is, so the preview is as long as what is sent.
 */
export function storefrontTextSampleValues(
	kind: StorefrontTextMessageKind,
	brandName: string,
	storefrontHost?: string | null,
): Record<string, string> {
	const definition = storefrontTextMessageDefinition(kind);
	const values: Record<string, string> = {};
	for (const variable of definition.variables) {
		values[variable.name] = variable.sampleValue;
	}
	values.brandName = brandName;
	if (
		storefrontHost != null &&
		storefrontHost !== '' &&
		definition.linkVariable != null
	) {
		values[definition.linkVariable] =
			`https://${storefrontHost}/s/${SAMPLE_SHORT_LINK_CODE}`;
	}
	return values;
}

/**
 * A text as the customer would receive it, filled with sample values: what
 * the Brand page previews and counts segments on. The sign-in text gets the
 * autofill line the sender adds, so the count includes it. The storefront's
 * host, when the page knows it, names the autofill line and the sample link;
 * otherwise a sample host does.
 */
export function storefrontTextPreview(
	kind: StorefrontTextMessageKind,
	body: string,
	brandName: string,
	storefrontHost?: string | null,
): string {
	const rendered = renderStorefrontPlainText(
		body,
		storefrontTextSampleValues(kind, brandName, storefrontHost),
	);
	const host =
		storefrontHost != null && storefrontHost !== ''
			? storefrontHost
			: STOREFRONT_SAMPLE_OTP_HOST;
	if (kind === 'sign_in') {
		return appendSmsOtpLine(rendered, STOREFRONT_SAMPLE_CODE, host);
	}
	// PD-17379 — the Android app's text also carries the app's tag line.
	return kind === 'sign_in_android'
		? appendSmsAutofillLines(
				rendered,
				STOREFRONT_SAMPLE_CODE,
				host,
				SAMPLE_ANDROID_APP_HASH,
			)
		: rendered;
}

// --- Android app code autofill (PD-17379) ---

/**
 * Google's SMS Retriever, which lets the Android storefront apps fill in a
 * texted sign-in code without the customer typing it, only hands the app a
 * text of at most 140 bytes that carries the app's hash.
 */
export const ANDROID_SMS_RETRIEVER_MAX_BYTES = 140;

/** UTF-8 byte length, counted by code point so it needs no TextEncoder. */
function utf8ByteLength(text: string): number {
	let bytes = 0;
	for (const character of text) {
		const codePoint = character.codePointAt(0) ?? 0;
		bytes +=
			codePoint < 0x80 ? 1 : codePoint < 0x800 ? 2 : codePoint < 0x10000 ? 3 : 4;
	}
	return bytes;
}

/**
 * A text's size as Google's 140-byte limit counts it. Google does not say
 * whether that is the text's UTF-8 bytes or the bytes of the single SMS it
 * travels in (160 standard characters, or 70 when a character outside the
 * standard set forces UCS-2), so this is the larger of the two: a text within
 * the limit by this count is within it either way.
 */
export function androidSmsRetrieverByteLength(text: string): number {
	const segment = countSmsSegments(text);
	const smsBytes =
		segment.encoding === 'GSM-7'
			? Math.ceil((segment.units * 7) / 8)
			: segment.units * 2;
	return Math.max(utf8ByteLength(text), smsBytes);
}

/** True when an Android app can be handed this text to fill the code in. */
export function fitsAndroidSmsRetriever(text: string): boolean {
	return androidSmsRetrieverByteLength(text) <= ANDROID_SMS_RETRIEVER_MAX_BYTES;
}

/** A stand-in hash, for measuring the line an Android app request adds. */
const SAMPLE_ANDROID_APP_HASH = 'AbCdEfGhIjK';

export interface StorefrontSignInAndroidFitModel {
	/** The text, sent to the Android app, is short enough to be filled in. */
	fits: boolean;
	/**
	 * The preview (the text with its `@host #code` line, as the Brand page
	 * shows and counts it) must stay under this many characters to fit. Exact
	 * for standard texting characters, and for a text sent as UCS-2; Chinese
	 * and other three-byte characters count for a little more.
	 */
	underCharacters: number;
}

/**
 * PD-17379 — whether a sign-in text can be filled in automatically when it is
 * sent to the Android app, measured on the same sample values the Brand page
 * previews. `kind` is the text the app is sent: the Android app text, or the
 * sign-in code text when the brand has turned the Android app text off.
 *
 * The app's text is the text with the app's tag line before the `@host #code`
 * line. A text that is too long is still sent; the customer types the code.
 */
export function storefrontSignInAndroidFit(
	kind: 'sign_in' | 'sign_in_android',
	body: string,
	brandName: string,
	storefrontHost?: string | null,
): StorefrontSignInAndroidFitModel {
	const host =
		storefrontHost != null && storefrontHost !== ''
			? storefrontHost
			: STOREFRONT_SAMPLE_OTP_HOST;
	const rendered = renderStorefrontPlainText(
		body,
		storefrontTextSampleValues(kind, brandName, storefrontHost),
	);
	const appText = appendSmsAutofillLines(
		rendered,
		STOREFRONT_SAMPLE_CODE,
		host,
		SAMPLE_ANDROID_APP_HASH,
	);
	// A text sent as UCS-2 (an emoji, a curly quote) takes two bytes a
	// character, which halves the budget.
	const bytesPerCharacter =
		countSmsSegments(appText).encoding === 'UCS-2' ? 2 : 1;
	const budget = Math.floor(ANDROID_SMS_RETRIEVER_MAX_BYTES / bytesPerCharacter);
	// The Android app text's preview already shows the tag line; the sign-in
	// code text's preview does not, so its budget is the limit less that line.
	const previewBudget =
		kind === 'sign_in_android'
			? budget
			: budget - `${SAMPLE_ANDROID_APP_HASH}\n`.length;

	return {
		fits: fitsAndroidSmsRetriever(appText),
		underCharacters: previewBudget + 1,
	};
}
